import React from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import { H2, H3, P } from '@decisiv/ui-components/lib/components/Typography';
import Pencil from '@decisiv/iconix/lib/components/Pencil';
import Copy from '@decisiv/iconix/lib/components/Copy';
import Download from '@decisiv/iconix/lib/components/Download';
import EllipsisV from '@decisiv/iconix/lib/components/EllipsisV';
import Lock from '@decisiv/iconix/lib/components/Lock';
import Share from '@decisiv/iconix/lib/components/Share';
import ExclamationTriangle from '@decisiv/iconix/lib/components/ExclamationTriangle';
import Plug from '@decisiv/iconix/lib/components/Plug';
import ExternalLink from '@decisiv/iconix/lib/components/ExternalLink';
import ArrowUp from '@decisiv/iconix/lib/components/ArrowUp';
import ChevronLeft from '@decisiv/iconix/lib/components/ChevronLeft';
import Plus from '@decisiv/iconix/lib/components/Plus';
import { currentVersion, fmtDate, getCategoryMeta } from './atUtils';
import SchemaViewer from './SchemaViewer';
import { ToolkitButton } from './style';
import authorizeOn from '../../utils/resourceAuthorizer';

function DetailHeader({
  action,
  version,
  allVersions,
  onVersionChange,
  onEdit,
  onCopy,
  onDownload,
  onMenu,
}) {
  const cat = getCategoryMeta(action.category);
  const CatIcon = cat.icon;
  // Edit and the overflow menu (delete/migrate/lock) are admin-only; Copy and
  // Download (export) stay open. A non-admin who mutates gets a 401 that logs
  // the session out (PD-3396), so disable the admin controls here.
  const canManage = authorizeOn();

  return (
    <div className="detail-header">
      <div className="head-top">
        <div className="head-left">
          <H2 className="action-name">{action.name}</H2>
          <div className="alias">{action.alias || '—'}</div>
        </div>
        <div className="head-right">
          <ToolkitButton
            kind="secondary"
            icon={Copy}
            text={t`Copy`}
            onClick={onCopy}
          />
          <ToolkitButton
            kind="secondary"
            icon={Download}
            text={t`Download`}
            onClick={onDownload}
          />
          <ToolkitButton
            kind="secondary"
            icon={Pencil}
            text={t`Edit`}
            onClick={onEdit}
            disabled={!canManage}
          />
          <ToolkitButton
            kind="secondary"
            icon={EllipsisV}
            onClick={onMenu}
            aria-label={t`More actions`}
            disabled={!canManage}
          />
        </div>
      </div>
      <div className="head-meta">
        <span
          className="tag"
          style={{ '--tag-bg': cat.bg, '--tag-color': cat.color }}
        >
          <span className="tag-dot">
            <CatIcon size="small" />
          </span>
          {cat.name}
        </span>
        <div className="version-pills" role="tablist" aria-label="Version">
          {allVersions.map((v) => {
            const active = v.version === version.version;
            let title = `v${v.version}`;
            if (v.locked) title = `v${v.version} (locked)`;
            else if (v.isCurrent) title = `v${v.version} (current)`;
            return (
              <button
                type="button"
                key={v.version}
                className={`version-pill ${active ? 'active' : ''} ${
                  active && v.locked ? 'locked-version' : ''
                }`}
                onClick={() => onVersionChange(v.version)}
                title={title}
              >
                v{v.version}
                {v.isCurrent && <span className="current-glyph">CUR</span>}
                {v.locked && (
                  <span className="lock-glyph">
                    <Lock size="small" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <span className="muted small">
          {t`Created`} {fmtDate(version.createdAt)} {t`by`} {version.createdBy}
        </span>
      </div>
    </div>
  );
}

DetailHeader.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  action: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  version: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  allVersions: PropTypes.array.isRequired,
  onVersionChange: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onMenu: PropTypes.func.isRequired,
};

function DetailTabs({ active, onChange, version, totalVersions }) {
  return (
    <div className="detail-tabs">
      <button
        type="button"
        className={`detail-tab ${active === 'details' ? 'active' : ''}`}
        onClick={() => onChange('details')}
      >
        {t`Details`}
      </button>
      <button
        type="button"
        className={`detail-tab ${active === 'integrations' ? 'active' : ''}`}
        onClick={() => onChange('integrations')}
      >
        {t`Integrations`}{' '}
        <span className="count-badge">{version.integrations.length}</span>
      </button>
      <button
        type="button"
        className={`detail-tab ${active === 'versions' ? 'active' : ''}`}
        onClick={() => onChange('versions')}
      >
        {t`Versions`} <span className="count-badge">{totalVersions}</span>
      </button>
    </div>
  );
}

DetailTabs.propTypes = {
  active: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  version: PropTypes.object.isRequired,
  totalVersions: PropTypes.number.isRequired,
};

function DetailsTab({ action, version }) {
  return (
    <>
      {(() => {
        // Prefer the selected version's values; fall back to the action for
        // legacy rows that carry them only on one row.
        const alias = version.alias || action.alias;
        const description = version.description || action.description;
        return (
          <>
            <div className="detail-section">
              <H3>{t`Alias`}</H3>
              {alias ? (
                <div className="desc-text">{alias}</div>
              ) : (
                <div className="desc-text desc-empty">{t`No alias yet.`}</div>
              )}
            </div>
            <div className="detail-section">
              <H3>{t`Description`}</H3>
              {description ? (
                <div className="desc-text">{description}</div>
              ) : (
                <div className="desc-text desc-empty">{t`No description yet.`}</div>
              )}
            </div>
          </>
        );
      })()}
      {version.changeNote && (
        <div className="detail-section">
          <H3>{t`Change Notes (v${version.version})`}</H3>
          <div className="desc-text">{version.changeNote}</div>
        </div>
      )}
      <div className="detail-section">
        <H3>{t`Input Schema`}</H3>
        <SchemaViewer label="Input" schema={version.inputSchema} />
      </div>
      <div className="detail-section">
        <H3>{t`Output Schema`}</H3>
        <SchemaViewer label="Output" schema={version.outputSchema} />
      </div>
      <div className="detail-section">
        <H3>{t`Integration Metadata Schema`}</H3>
        {version.metadataSchema &&
        Object.keys(version.metadataSchema).length > 0 ? (
          <SchemaViewer label="Metadata" schema={version.metadataSchema} />
        ) : (
          <div className="desc-text desc-empty">
            {t`No integration metadata declared.`}
          </div>
        )}
      </div>
    </>
  );
}

DetailsTab.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  action: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  version: PropTypes.object.isRequired,
};

function IntegrationsTab({ version }) {
  if (version.integrations.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-icon">
          <Plug size="medium" />
        </div>
        <div className="empty-title">
          {t`No integrations currently use this action`}
        </div>
        <div>
          {t`Assign this action from the integration builder. Locked actions are hidden from new assignments.`}
        </div>
      </div>
    );
  }
  const idByName = version.integrationIdByName || {};
  return (
    <div className="int-list">
      {version.integrations.map((i) => {
        const integrationId = idByName[i];
        return (
          <div className="int-row" key={i}>
            <span className="int-icon">
              <Plug size="medium" />
            </span>
            <div>
              <div className="int-name">{i}</div>
              <div className="int-meta">
                {t`Assigned`} · v{version.version}
              </div>
            </div>
            <div className="int-grow" />
            {integrationId && (
              <a
                className="btn btn-ghost btn-sm"
                href={`/integrations/${integrationId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink size="small" /> {t`Open`}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

IntegrationsTab.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  version: PropTypes.object.isRequired,
};

function VersionsTab({
  action,
  currentSelectedVersion,
  onSelectVersion,
  onMigrateFromVersion,
  onCompare,
}) {
  const sortedVersions = [...action.versions].sort(
    (a, b) => b.version - a.version,
  );
  const targetVersion = sortedVersions.find((v) => v.isCurrent);
  // Migrate is admin-only (a non-admin gets logged out on 401, PD-3396);
  // Compare is read-only and stays enabled.
  const canManage = authorizeOn();

  return (
    <>
      {action.versions.length > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 10,
          }}
        >
          <ToolkitButton
            kind="secondary"
            size="small"
            icon={Share}
            text={t`Compare versions`}
            onClick={() =>
              onCompare(
                action.versions[action.versions.length - 2].version,
                currentVersion(action).version,
              )
            }
          />
        </div>
      )}
      <div className="version-timeline">
        {action.versions.map((v) => (
          <div className="timeline-node" key={v.version}>
            <span
              className={`timeline-dot ${v.isCurrent ? 'current' : ''} ${
                v.locked && !v.isCurrent ? 'locked' : ''
              }`}
            >
              v{v.version}
            </span>
            <div className="timeline-label">
              v{v.version}{' '}
              {v.isCurrent && (
                <span style={{ color: 'var(--kds-accent)', fontSize: 10 }}>
                  · {t`current`}
                </span>
              )}
            </div>
            <div className="timeline-meta">
              {fmtDate(v.createdAt)} · {v.integrations.length}{' '}
              {t`integration${v.integrations.length === 1 ? '' : 's'}`}
              {v.locked && (
                <>
                  {' '}
                  ·{' '}
                  <span style={{ color: 'var(--kds-text-3)' }}>
                    {t`locked`}
                  </span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      <table className="versions-table">
        <thead>
          <tr>
            <th>{t`Version`}</th>
            <th>{t`Status`}</th>
            <th>{t`Created`}</th>
            <th>{t`Integrations`}</th>
            <th aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {sortedVersions.map((v) => (
            <tr
              key={v.version}
              className={
                v.version === currentSelectedVersion ? 'current-row' : ''
              }
              style={{ cursor: 'pointer' }}
              onClick={() => onSelectVersion(v.version)}
            >
              <td>
                <span className="v-label">
                  v{v.version}
                  {v.locked && <Lock size="small" color="var(--if-ink-2)" />}
                </span>
              </td>
              <td>
                {v.isCurrent && (
                  <span className="status-pill status-current">
                    {t`Current`}
                  </span>
                )}
                {!v.isCurrent && v.locked && (
                  <span className="status-pill status-locked">
                    <Lock size="small" /> {t`Locked`}
                  </span>
                )}
                {!v.isCurrent && !v.locked && (
                  <span className="status-pill status-prev">{t`Previous`}</span>
                )}
              </td>
              <td className="muted small">{fmtDate(v.createdAt)}</td>
              <td>{v.integrations.length}</td>
              <td style={{ textAlign: 'right' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    gap: 6,
                    alignItems: 'center',
                  }}
                >
                  {!v.isCurrent && targetVersion && (
                    <ToolkitButton
                      variant="ghost"
                      size="small"
                      icon={Share}
                      text={t`Compare`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCompare(v.version, targetVersion.version);
                      }}
                    />
                  )}
                  {!v.isCurrent && v.integrations.length > 0 && targetVersion && (
                    <ToolkitButton
                      kind="secondary"
                      size="small"
                      icon={ArrowUp}
                      text={t`Migrate to v${targetVersion.version}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMigrateFromVersion(v.version);
                      }}
                      disabled={!canManage}
                    />
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

VersionsTab.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  action: PropTypes.object.isRequired,
  currentSelectedVersion: PropTypes.number.isRequired,
  onSelectVersion: PropTypes.func.isRequired,
  onMigrateFromVersion: PropTypes.func.isRequired,
  onCompare: PropTypes.func.isRequired,
};

export default function DetailPane({
  action,
  version,
  tab,
  onTabChange,
  onVersionChange,
  onEdit,
  onCopy,
  onDownload,
  onMenu,
  onMigrateFromVersion,
  onCompare,
}) {
  const allVersions = action.versions;
  const currentLatest = currentVersion(action);
  const viewingOldButHasRemnants =
    !version.isCurrent && version.integrations.length > 0 && currentLatest;
  // The banner's Migrate is admin-only (non-admin gets logged out on 401,
  // PD-3396); disable it for non-admins.
  const canManage = authorizeOn();

  return (
    <section className="detail-pane">
      <DetailHeader
        action={action}
        version={version}
        allVersions={allVersions}
        onVersionChange={onVersionChange}
        onEdit={onEdit}
        onCopy={onCopy}
        onDownload={onDownload}
        onMenu={onMenu}
      />
      <div style={{ padding: '0 28px' }}>
        <DetailTabs
          active={tab}
          onChange={onTabChange}
          version={version}
          totalVersions={action.versions.length}
        />
      </div>
      <div className="detail-body">
        {viewingOldButHasRemnants && (
          <div className="banner banner-warn">
            <span className="banner-icon">
              <ExclamationTriangle size="medium" />
            </span>
            <div className="banner-body">
              <strong>
                {version.integrations.length} integration
                {version.integrations.length === 1 ? '' : 's'} still on v
                {version.version}.
              </strong>{' '}
              A newer version (v{currentLatest.version}) is available. Migrate
              to keep contracts in sync.
            </div>
            <ToolkitButton
              kind="secondary"
              size="small"
              text={t`Migrate to v${currentLatest.version}`}
              onClick={() => onMigrateFromVersion(version.version)}
              disabled={!canManage}
            />
          </div>
        )}
        {tab === 'details' && <DetailsTab action={action} version={version} />}
        {tab === 'integrations' && <IntegrationsTab version={version} />}
        {tab === 'versions' && (
          <VersionsTab
            action={action}
            currentSelectedVersion={version.version}
            onSelectVersion={onVersionChange}
            onMigrateFromVersion={onMigrateFromVersion}
            onCompare={onCompare}
          />
        )}
      </div>
    </section>
  );
}

DetailPane.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  action: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  version: PropTypes.object.isRequired,
  tab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onVersionChange: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onMenu: PropTypes.func.isRequired,
  onMigrateFromVersion: PropTypes.func.isRequired,
  onCompare: PropTypes.func.isRequired,
};

export function EmptyDetail({ onCreate }) {
  // Create is admin-only (non-admin gets logged out on 401, PD-3396).
  const canManage = authorizeOn();

  return (
    <section className="detail-pane">
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 48,
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: 380 }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'var(--kds-accent-soft)',
              color: 'var(--kds-accent)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 16,
            }}
          >
            <Share size="large" />
          </div>
          <H2 style={{ fontSize: 18, margin: '0 0 6px 0' }}>
            {t`No actions yet`}
          </H2>
          <P style={{ color: 'var(--kds-text-3)', fontSize: 14, marginTop: 0 }}>
            {t`Actions define the input/output contract used by integration commands. Create your first one to get started.`}
          </P>
          <div style={{ marginTop: 14 }}>
            <ToolkitButton
              kind="primary"
              icon={Plus}
              text={t`Create Action`}
              onClick={onCreate}
              disabled={!canManage}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

EmptyDetail.propTypes = {
  onCreate: PropTypes.func.isRequired,
};

export function NoSelection() {
  return (
    <section className="detail-pane">
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--kds-text-3)',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <ChevronLeft size="medium" color="var(--if-ink-3)" />
          <div
            style={{ marginTop: 6 }}
          >{t`Select an action from the list.`}</div>
        </div>
      </div>
    </section>
  );
}
